// Mounts the self-driving evaluator: shortly after a session opens and hourly
// after, if a local engine (Ollama-first) is up and the daily cadence is due,
// run the evaluator and auto-apply its gated proposals to .apolorules. Best-
// effort and silent — the LearnedRules panel surfaces what landed.

import { useEffect } from 'react';
import {
  shouldAutoRunEvaluator,
  runAndApplyEvaluator,
  applyStashedEvaluatorProposals,
} from '../lib/evaluatorAutoRun.js';
import { resolveEvaluatorEngine } from '../lib/evaluatorLocal.js';

const LS_KEY = 'apolo:evaluator:last-auto-run';
// Separate cadence for the SERVER-stashed proposals (E3-T02). Daily-gated on its
// own key so it neither spams nor piggybacks on the local-run key (the local run
// needs Ollama up; this one does NOT — it just applies rules already produced in
// the cloud while the app was closed).
const LS_KEY_STASHED = 'apolo:evaluator:last-stash-apply';
const CHECK_MS = 60 * 60 * 1000; // hourly check; runAndApply is itself daily-gated

export function useEvaluatorAutoRun(root, sessionId) {
  useEffect(() => {
    if (!root || !String(root).startsWith('/') || !sessionId) return undefined;
    let cancelled = false;

    // Apply any rules the server-side cloud evaluator stashed while the app was
    // closed (E3-T02). Runs INDEPENDENTLY of the local engine — the rules are
    // already cloud-produced + double-gated — but stays daily-gated so it does
    // not re-apply on every tick.
    const applyStashed = async () => {
      if (cancelled) return;
      let last = 0;
      try { last = Number(localStorage.getItem(LS_KEY_STASHED)) || 0; } catch { last = 0; }
      if (!shouldAutoRunEvaluator({ lastRunAt: last, now: Date.now() })) return;
      try {
        const res = await applyStashedEvaluatorProposals(root, sessionId);
        // Mark the cadence on any clean run (applied OR empty) so an empty stash
        // doesn't get re-checked every tick; a hard failure leaves it to retry.
        if (res?.ok) {
          try { localStorage.setItem(LS_KEY_STASHED, String(Date.now())); } catch { /* noop */ }
        }
      } catch { /* best-effort — never disturb the session */ }
    };

    const tick = async () => {
      if (cancelled) return;
      // Server-stashed proposals first (no local engine required).
      await applyStashed();
      // Then the preferred local engine run (Ollama-first), if it's up + due.
      if (!resolveEvaluatorEngine()) return;
      let last = 0;
      try { last = Number(localStorage.getItem(LS_KEY)) || 0; } catch { last = 0; }
      if (!shouldAutoRunEvaluator({ lastRunAt: last, now: Date.now() })) return;
      try {
        const res = await runAndApplyEvaluator(root, sessionId);
        if (res?.ok) {
          try { localStorage.setItem(LS_KEY, String(Date.now())); } catch { /* noop */ }
        }
      } catch { /* best-effort — never disturb the session */ }
    };

    const warmup = setTimeout(tick, 8000);
    const id = setInterval(tick, CHECK_MS);
    return () => { cancelled = true; clearTimeout(warmup); clearInterval(id); };
  }, [root, sessionId]);
}
